import { FC, useEffect, useState, useMemo } from "react"
import { Column, useTable } from "react-table"
import { User } from "../../models/user"
import AdminUserService from "../../services/admin/adminUser"
import UserService from "../../services/user"
import HelperService from "../../services/helper"
import useNotyf from "../../context/useNotify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCrown, faTimes } from "@fortawesome/free-solid-svg-icons"
import ReactPaginate from "react-paginate"
import { Meta, QueryParams } from "../../models/misc"
import { StyledInstructions, StyledPagination, StyledTable } from "./Styles"
import instructions from "../../content-options/admin/instructions"
import { useDebounce } from "../../hooks/hooks"
import Placeholder from "../../images/common/vgc-logo.png"

const UserTable: FC = () => {
  const notyf = useNotyf()

  // Data
  const [users, setUsers] = useState<User[]>([])

  const [meta, setMeta] = useState<Meta>({
    totalPages: 0,
    page: 1,
    pageSize: 25,
  })
  const [search, setSearch] = useState("")
  const [premiumFilter, setPremiumFilter] = useState<boolean | undefined>(undefined)
  const debouncedSearch = useDebounce(search)

  // API
  const fetchUsers = async (page: number = 1) => {
    try {
      const filterObj: QueryParams = {
        page: page,
        pageSize: meta.pageSize,
        username: search,
        isPremium: premiumFilter,
      }
      const data = await UserService.readUsers(filterObj)
      setUsers(data.data)
      setMeta(data.meta)
    } catch (err) {
      console.log("Error fetching users: ", err)
      HelperService.showNotfyErr(notyf, err, "Error fetching users.")
    }
  }

  const updateUser = async (userId: string, updateObj: Partial<User>) => {
    try {
      await AdminUserService.adminUpdateUser(userId, updateObj)
      notyf.success("User updated successfully.")
      fetchUsers(meta.page)
    } catch (err) {
      console.log("Error updating user: ", err)
      HelperService.showNotfyErr(notyf, err, "Error updating user.")
    }
  }

  // Methods
  const handleTogglePremium = async (user: User) => {
    updateUser(user._id, { isPremium: !user.isPremium })
  }

  const handleInputChange = (value: string) => {
    setSearch(value)
  }

  // Hooks
  const [isPageLoaded, setIsPageLoaded] = useState(false)

  useEffect(() => {
    if (isPageLoaded) {
      return
    }
    fetchUsers()
    setIsPageLoaded(true)
  }, [isPageLoaded])

  useEffect(() => {
    if (!isPageLoaded) {
      return
    }
    fetchUsers()
  }, [debouncedSearch, isPageLoaded, premiumFilter])

  // Table Data
  const columns: Column<User>[] = useMemo(
    () => [
      {
        Header: "Username",
        accessor: "username",
        Cell: ({ row }) => (
          <div className='d-flex align-items-center'>
            <img
              src={row.original.profileImage || Placeholder}
              alt={row.original.username}
              className='img-fluid me-2' // Bootstrap classes for responsive image and margin
              style={{ maxWidth: "30px", maxHeight: "30px", borderRadius: "50%" }} // Adjust size and make it circular
            />
            {row.original.username}
          </div>
        ),
      },
      {
        Header: "Premium",
        accessor: "isPremium",
        Cell: ({ row }) => (
          <button
            className='btn'
            onClick={() => handleTogglePremium(row.original)}
            style={{ background: "none", border: "none" }}
          >
            <FontAwesomeIcon
              icon={faCrown}
              style={{ color: row.original.isPremium ? "gold" : "lightgray" }}
            />
          </button>
        ),
      },
      {
        Header: "Leagues",
        accessor: (row) => row.leagues.length,
        id: "leaguesCount",
      },
      {
        Header: "Twitch Link",
        accessor: "twitch",
      },
      {
        Header: "YouTube Link",
        accessor: "youtube",
      },
      {
        Header: "Position",
        accessor: "position",
      },
    ],
    [users]
  )
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: users,
  })

  return (
    <>
      <div className='d-flex flex-column mb-3'>
        <StyledInstructions>{instructions.table.user}</StyledInstructions>
        <div className='d-flex justify-content-end gap-2 my-2'>
          {/* Premium filter */}
          <div className='d-flex align-items-center'>
            <div className='btn-group'>
              <button
                className={`btn btn-sm ${premiumFilter === true ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setPremiumFilter(premiumFilter === true ? undefined : true)}
              >
                Premium
              </button>
              <button
                className={`btn btn-sm ${
                  premiumFilter === false ? "btn-primary" : "btn-secondary"
                }`}
                onClick={() => setPremiumFilter(premiumFilter === false ? undefined : false)}
              >
                Non-Premium
              </button>
            </div>
          </div>
          {/*  Search */}
          <div className='input-container'>
            <input
              type='text'
              className='form-control bg-dark text-white'
              placeholder='Enter player name...'
              value={search}
              onChange={(e) => handleInputChange(e.target.value)}
            />
            {search && (
              <FontAwesomeIcon
                icon={faTimes}
                className='text-white clear-icon'
                onClick={() => setSearch("")}
              />
            )}
          </div>
        </div>
        <div className='table-responsive'>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    })}
                  </tr>
                )
              })}
            </tbody>
          </StyledTable>
        </div>
      </div>
      <div className='d-flex justify-content-end'>
        {users.length ? (
          <StyledPagination>
            <ReactPaginate
              previousLabel={"Previous"}
              nextLabel={"Next"}
              pageCount={meta.totalPages}
              onPageChange={({ selected }) => {
                fetchUsers(selected + 1)
              }}
              containerClassName={"pagination"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              nextClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextLinkClassName={"page-link"}
              activeClassName={"active"}
            />
          </StyledPagination>
        ) : null}
      </div>
    </>
  )
}

export default UserTable
